import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Switch, Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { KudosCompanyCountryVisibilityDto, KudosCompanyCountryVisibilityFilterInput } from '../../../types'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import EditKudosCompanyCountryVisibilityModal from '../../../components/custom_components/KudosVisibilityModal/EditKudosCompanyCountryVisibilityModal'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import DeleteKudosCompanyCountryVisibilityModal from '../../../components/custom_components/DeleteKudosModal/DeleteKudosCompanyCountryVisibilityModal'
import { getCountriesWhereUserIsHrAdmin, isHRAdminOfThisCountry } from '../../user/util'
import { useAuth } from '../../../context/auth-context'

interface KudosCompanyVisibilityTableProps {
  kudosCompanyCountryVisibilityList?: KudosCompanyCountryVisibilityDto[]
  loading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  filters: KudosCompanyCountryVisibilityFilterInput
  showMoreButtonVisible: boolean
  resetFiltersAfterVisibilityChange: () => void
}

export const KudosCompanyCountryVisibilityTable: FC<KudosCompanyVisibilityTableProps> = ({
  kudosCompanyCountryVisibilityList,
  loading,
  isLoadingMore,
  error,
  filters,
  showMoreButtonVisible,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [kudosCompanyCountryVisibilityToEdit, setKudosCompanyCountryVisibilityToEdit] = useState<KudosCompanyCountryVisibilityDto | null>(
    null,
  )
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [kudosCompanyCountryVisibilityToDelete, setKudosCompanyCountryVisibilityToDelete] =
    useState<KudosCompanyCountryVisibilityDto | null>(null)

  const hrAdminCountries = user.roles ? getCountriesWhereUserIsHrAdmin(user.roles) : []

  if ((loading && !isLoadingMore) || error || !kudosCompanyCountryVisibilityList) {
    return (
      <BackgroundBox r height={'withTabsAndInfoBox'}>
        <NewLoadingAndErrors
          isLoading={loading && !isLoadingMore}
          error={error}
          hasNoData={!kudosCompanyCountryVisibilityList}
          showSpinner
          t={t}
        />
      </BackgroundBox>
    )
  }

  const handleEditClick = (kudosCompanyVisibility: KudosCompanyCountryVisibilityDto) => {
    setKudosCompanyCountryVisibilityToEdit(kudosCompanyVisibility)
    setEditModalVisible(true)
  }

  const handleEditClose = () => {
    setEditModalVisible(false)
    setKudosCompanyCountryVisibilityToEdit(null)
  }

  const handleDeleteClick = (kudos: KudosCompanyCountryVisibilityDto) => {
    setKudosCompanyCountryVisibilityToDelete(kudos)
    setDeleteModalVisible(true)
  }
  const handleDeleteClose = () => {
    setDeleteModalVisible(false)
    setKudosCompanyCountryVisibilityToDelete(null)
  }

  const columns: any = [
    {
      title: t('kudos.table.company'),
      key: 'companyName',
      width: '40%',
      render: (record: KudosCompanyCountryVisibilityDto) => <PerText fontSize="m">{record.companyName}</PerText>,
    },
    {
      title: t('kudos.table.country'),
      key: 'country',
      width: '20%',
      render: (record: KudosCompanyCountryVisibilityDto) => <PerText fontSize="m">{record.country}</PerText>,
    },
    {
      title: t('kudos.table.visibility'),
      key: 'kudosEnabled',
      width: hrAdminCountries.length > 0 ? '25%' : '40%',
      render: (record: KudosCompanyCountryVisibilityDto) => (
        <Switch
          style={!isHRAdminOfThisCountry(record.country, !!user.roles ? user.roles : []) ? { cursor: 'not-allowed' } : undefined}
          checked={record.kudosEnabled}
          disabled={!isHRAdminOfThisCountry(record.country, !!user.roles ? user.roles : [])}
          onChange={() => {
            handleEditClick(record)
          }}
        />
      ),
    },
  ]
  if (hrAdminCountries.length > 0) {
    columns.push({
      title: t('kudos.table.actions'),
      key: 'actions',
      width: '15%',
      className: 'textAlign-right',
      render: (record: KudosCompanyCountryVisibilityDto) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'delete_fill',
              color: 'error',
              tooltip: t('kudos.table.delete'),
              onClick: () => handleDeleteClick(record),
              showOnlyWhen: isHRAdminOfThisCountry(record.country, !!user.roles ? user.roles : []),
            },
          ]}
        />
      ),
    })
  }

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithNoSortingAndTabsAndInfoBoxAndShowMoreButton: showMoreButtonVisible,
          viewWithNoSortingAndTabsAndInfoBox: !showMoreButtonVisible,
        })}
        columns={columns}
        dataSource={kudosCompanyCountryVisibilityList.map((kudos) => ({ ...kudos, key: kudos.id }))}
        pagination={false}
        rowKey="id"
        rowClassName={() => 'minHeight-row'}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              heading={t('kudos.table.noCompanyCountryVisibilityWithFilters')}
              info={t('kudos.table.noCompanyCountryVisibilityWithFiltersInfo')}
              icon
            />
          ),
        }}
      />

      <EditKudosCompanyCountryVisibilityModal
        visible={editModalVisible}
        onClose={handleEditClose}
        kudosCompanyCountryVisibility={kudosCompanyCountryVisibilityToEdit}
        filters={filters}
        resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
      />
      <DeleteKudosCompanyCountryVisibilityModal
        visible={deleteModalVisible}
        onClose={handleDeleteClose}
        kudosCompanyCountryVisibilityDto={kudosCompanyCountryVisibilityToDelete}
        filters={filters}
        resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
      />
    </>
  )
}
